import { useEffect } from 'react';
import useGameStore, { floatSharesOf, rentFor, rentYieldOf } from '../store/gameStore';
import type { PropertyTile } from '../types/gameTypes';
import { money, percent } from '../lib/format';
import { tileName as localizeTile, useT } from '../i18n';
import { DISTRICT_META } from './districtMeta';
import { DistrictIcon } from './GameIcons';

/** Section key per tile flavour (transit / city hall / financial desk / start / commercial). */
const guideSectionFor = (tile: PropertyTile): 'transit' | 'cityHall' | 'financial' | 'payroll' | 'commercial' => {
  if (tile.tileType === 'TRANSIT') return 'transit';
  if (tile.tileType === 'FINANCIAL') return 'financial';
  if (tile.name.includes('Audit')) return 'cityHall';
  if (tile.name.includes('Payroll')) return 'payroll';
  return 'commercial';
};

const BULLETS: Record<string, string[]> = {
  transit: ['guide.transit.b1', 'guide.transit.b2', 'guide.transit.b3'],
  cityHall: ['guide.cityHall.b1', 'guide.cityHall.b2', 'guide.cityHall.b3'],
  financial: ['guide.financial.b1', 'guide.financial.b2', 'guide.financial.b3', 'guide.financial.b4'],
  payroll: ['guide.payroll.b1', 'guide.payroll.b2', 'guide.payroll.b3'],
  commercial: ['guide.commercial.b1', 'guide.commercial.b2'],
};

const HUE: Record<string, string> = {
  transit: '#22d3ee',
  cityHall: '#fbbf24',
  financial: '#34d399',
  payroll: '#f472b6',
  commercial: '#a78bfa',
};


/**
 * Interactive explainer for special tiles: title + "Jak toto pole funguje / How this
 * tile works", exact numbers, and the tactical edge. Opened by clicking a special tile.
 */
export const TileGuideModal = () => {
  const t = useT();
  const store = useGameStore();
  const { board, guideTileId, market, players, currentPlayerIndex } = store;
  const tile = guideTileId ? board.find((entry) => entry.id === guideTileId) ?? null : null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') store.closeTileGuide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);

  if (!tile) return null;
  const section = guideSectionFor(tile);
  const accent = HUE[section];
  const meta = DISTRICT_META[tile.district];
  const visitor = players[currentPlayerIndex];
  const liveRent = rentFor(tile, market.phase);
  const yieldPct = percent(rentYieldOf(tile) * 100, 1);
  const float = floatSharesOf(tile);
  const human = players.find((p) => !p.isAI);
  const myShares = human ? tile.shares[human.id] ?? 0 : 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      onClick={() => store.closeTileGuide()}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border-2 bg-slate-900 shadow-2xl"
        style={{ borderColor: `${accent}99` }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3" style={{ background: `linear-gradient(90deg, ${accent}33, rgba(2,6,23,0.9))` }}>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>
              <DistrictIcon district={tile.district} size={14} /> {meta.label}
            </p>
            <h2 className="text-lg font-black text-white">{t(`guide.${section}.title`)}</h2>
            <p className="text-[11px] font-semibold text-slate-300">{localizeTile(tile.name, t)}</p>
          </div>
          <button
            type="button"
            onClick={() => store.closeTileGuide()}
            className="shrink-0 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700"
          >
            {t('common.close')} / ESC
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t('guide.title')}</p>
          <ul className="space-y-2">
            {BULLETS[section].map((key) => (
              <li key={key} className="flex gap-2 rounded-xl border border-slate-700/60 bg-slate-950/60 px-2.5 py-2 text-[11px] leading-snug text-slate-200">
                <span style={{ color: accent }}>▸</span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-xl border p-2.5 text-[11px]" style={{ borderColor: `${accent}55`, backgroundColor: `${accent}12` }}>
            <p className="font-black" style={{ color: accent }}>
              {t('guide.tactic')}
            </p>
            <p className="mt-1 text-slate-200">{t(`guide.${section}.tactic`)}</p>
          </div>

          {/* Live board numbers so the guide explains THIS tile, not a generic one. */}
          <div className="grid grid-cols-3 gap-2 text-center">
            {tile.tileType === 'COMMERCIAL' ? (
              <>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.rent')}</p>
                  <p className="font-mono text-sm font-black text-white">{money(liveRent)}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.yield')}</p>
                  <p className="font-mono text-sm font-black text-violet-200">{yieldPct}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.publicFloat')}</p>
                  <p className="font-mono text-sm font-black text-slate-200">{float}</p>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.cash')}</p>
                  <p className="truncate font-mono text-sm font-black text-white">{visitor ? money(visitor.cash) : '—'}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.round')}</p>
                  <p className="font-mono text-sm font-black text-amber-200">{market.currentRound}</p>
                </div>
                <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 px-2 py-1.5">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500">{t('common.phase')}</p>
                  <p className="font-mono text-sm font-black text-cyan-200">{market.phase}</p>
                </div>
              </>
            )}
          </div>

          {myShares > 0 && (
            <p className="text-[10px] font-bold text-emerald-300">
              {t('common.you')}: {myShares} {t('common.shares')} · {percent((myShares / Math.max(1, tile.totalShares)) * 100, 1)}
            </p>
          )}
          <p className="text-[10px] text-slate-500">{t('guide.tapHint')}</p>
        </div>
      </div>
    </div>
  );
};

export default TileGuideModal;

